import React, { useCallback, useEffect, useState } from 'react';
import { ISO3to2Mapping } from '../../../shared/countries.js';
import {
  CAPABILITIES,
  ROLES,
  ROLE_LABELS,
  can,
  type AccountDto,
  type AccountStatus,
  type CountryOption,
  type CreateAccountBody,
  type Role,
  type UpdateAccountBody,
} from '../../../shared/types/index.js';
import {
  ApiError,
  createAccount,
  deleteAccount,
  fetchAccounts,
  fetchCountries,
  provisionAccount,
  updateAccount,
} from '../../api/index.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { useSession } from '../../session/SessionProvider.js';
import { getFlagEmoji } from '../../utils/Helpers.js';
import Spinner from '../common/Spinner.js';
import CountriesField from './CountriesField.js';
import { InputField, SelectField } from './Field.js';

type Mode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; account: AccountDto };

// Same rules as server/src/validators/{auth,accounts}.ts
const USERNAME_PATTERN = /^[a-z0-9._-]{3,40}$/;
const MIN_PASSWORD_LENGTH = 8;

interface Draft {
  username: string;
  displayName: string;
  password: string;
  role: Role;
  status: AccountStatus;
  countries: Array<string>;
  upstreamUserId: string;
  /** The acting admin's own password, required to set a new password on an existing account. */
  currentPassword: string;
}

const EMPTY_DRAFT: Draft = {
  username: '',
  displayName: '',
  password: '',
  role: 'analyst',
  status: 'active',
  countries: [],
  upstreamUserId: '',
  currentPassword: '',
};

function draftFrom(account: AccountDto): Draft {
  return { ...EMPTY_DRAFT, displayName: account.displayName, role: account.role, status: account.status, countries: account.countries };
}

/** Human-readable API failure: validation details are listed, other errors show their message. */
function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.isValidationError) {
      return (err.data as Array<{ message: string }>).map((d) => d.message).join('; ');
    }
    return err.message;
  }
  return (err as Error)?.message ?? fallback;
}

function flags(countries: ReadonlyArray<string>): string {
  return countries.map((iso3) => (ISO3to2Mapping[iso3] ? getFlagEmoji(ISO3to2Mapping[iso3]) : iso3)).join(' ');
}

interface AccountsTabProps {
  /** Called after an impersonation started, e.g. to close the settings overlay. */
  onImpersonated?: () => void;
}

function AccountsTab({ onImpersonated }: AccountsTabProps) {
  const { t } = useTranslation();
  const { account: me, impersonate } = useSession();
  const canImpersonate = !!me && can(me.role, 'impersonate');
  const [accounts, setAccounts] = useState<Array<AccountDto> | null>(null);
  const [countries, setCountries] = useState<Array<CountryOption>>([]);
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Deletion is a two-step action: the first click only arms the confirm button. */
  const [confirmDelete, setConfirmDelete] = useState(false);

  const reload = useCallback(async () => {
    try {
      setAccounts(await fetchAccounts());
    } catch (err) {
      setError(describeError(err, t('accounts.loadFailed')));
    }
  }, [t]);

  // Load once on mount; `t` is stable per language, so a language change simply reloads.
  useEffect(() => {
    void reload();
    fetchCountries()
      .then(setCountries)
      .catch((err) => setError(describeError(err, t('accounts.loadFailed'))));
  }, [reload, t]);

  const open = (next: Mode) => {
    setError('');
    setFormError('');
    setDraft(next.kind === 'edit' ? draftFrom(next.account) : EMPTY_DRAFT);
    setConfirmDelete(false);
    setMode(next);
  };
  const close = () => {
    setConfirmDelete(false);
    setMode({ kind: 'closed' });
  };
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const replace = (updated: AccountDto) =>
    setAccounts((list) => (list ?? []).map((a) => (a.id === updated.id ? updated : a)));

  async function run(action: () => Promise<void>, fallback: string, report: (message: string) => void = setError) {
    setBusy(true);
    report('');
    try {
      await action();
    } catch (err) {
      report(describeError(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  /** Mirrors the server rules so obvious mistakes are reported before the request. */
  function validateDraft(): string {
    if (mode.kind === 'create' && !USERNAME_PATTERN.test(draft.username.trim().toLowerCase())) return t('accounts.errorUsername');
    if (!draft.displayName.trim()) return t('accounts.errorDisplayName');
    const passwordRequired = mode.kind === 'create' || draft.password.length > 0;
    if (passwordRequired && draft.password.length < MIN_PASSWORD_LENGTH) return t('accounts.errorPasswordLength');
    if (mode.kind === 'edit' && draft.password && !draft.currentPassword) return t('accounts.errorCurrentPasswordRequired');
    const linking = mode.kind === 'create' && draft.upstreamUserId.trim().length > 0;
    if (!linking && draft.countries.length === 0) return t('accounts.errorCountriesRequired');
    return '';
  }

  const submit = () => {
    const problem = validateDraft();
    if (problem) {
      setFormError(problem);
      return;
    }
    return run(async () => {
      if (mode.kind === 'create') {
        const body: CreateAccountBody = {
          username: draft.username.trim().toLowerCase(),
          displayName: draft.displayName.trim(),
          password: draft.password,
          role: draft.role,
          countries: draft.countries,
          ...(draft.upstreamUserId.trim() ? { upstreamUserId: draft.upstreamUserId.trim() } : {}),
        };
        const created = await createAccount(body);
        setAccounts((list) => [...(list ?? []), created].sort((a, b) => a.username.localeCompare(b.username)));
        close();
      } else if (mode.kind === 'edit') {
        const body: UpdateAccountBody = {
          displayName: draft.displayName.trim(),
          role: draft.role,
          status: draft.status,
          countries: draft.countries,
          ...(draft.password ? { password: draft.password, currentPassword: draft.currentPassword } : {}),
        };
        replace(await updateAccount(mode.account.id, body));
        close();
      }
    }, t('accounts.saveFailed'), setFormError);
  };

  const remove = (account: AccountDto) =>
    run(
      async () => {
        await deleteAccount(account.id);
        setAccounts((list) => (list ?? []).filter((a) => a.id !== account.id));
        close();
      },
      t('accounts.deleteFailed'),
      setFormError
    );

  const retryProvision = (account: AccountDto) =>
    run(async () => replace(await provisionAccount(account.id)), t('accounts.saveFailed'));

  const loginAs = (account: AccountDto) =>
    run(async () => {
      await impersonate(account.id);
      onImpersonated?.();
    }, t('accounts.saveFailed'));

  const roleOptions = ROLES.map((role) => ({ value: role, label: t(`roles.${role}`) || ROLE_LABELS[role] }));
  const statusOptions: Array<{ value: AccountStatus; label: string }> = [
    { value: 'active', label: t('accounts.active') },
    { value: 'suspended', label: t('accounts.suspended') },
  ];

  return (
    <div className="sp on">
      <div className="sp-title">{t('accounts.title')}</div>
      <div className="sp-subtitle">{t('accounts.subtitle')}</div>

      {error && <div className="login-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sg">
        <div className="sg-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('accounts.list', { count: accounts?.length ?? 0 })}</span>
          <button className="um-btn" onClick={() => open({ kind: 'create' })} disabled={busy}>
            {t('accounts.create')}
          </button>
        </div>
        {accounts === null && <div className="status-meta">{t('accounts.loading')}</div>}
        {accounts?.map((a) => (
          <div key={a.id} className="um-row">
            <div className="um-avatar">{a.displayName.charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="um-name">
                {a.displayName} <span style={{ color: 'var(--t3)', fontFamily: 'var(--mono)', fontSize: 10 }}>@{a.username}</span>
                {me?.id === a.id && <span style={{ color: 'var(--gold)', fontSize: 10, marginLeft: 6 }}>{t('accounts.you')}</span>}
              </div>
              <div className="um-role">
                {t(`roles.${a.role}`) || ROLE_LABELS[a.role]} · {flags(a.countries) || '—'} ·{' '}
                {a.status === 'active' ? t('accounts.active') : t('accounts.suspended')}
              </div>
              <div className="um-role" style={{ color: a.upstream.provisionError ? 'var(--red)' : 'var(--t3)' }}>
                {a.upstream.provisionError
                  ? `${t('accounts.provisionFailed')}: ${a.upstream.provisionError}`
                  : a.upstream.userId
                    ? a.upstream.linked
                      ? t('accounts.linked')
                      : t('accounts.provisioned')
                    : t('accounts.notProvisioned')}
              </div>
            </div>
            <div className="um-actions">
              {canImpersonate && me?.id !== a.id && a.status === 'active' && (
                <button className="um-btn" onClick={() => loginAs(a)} disabled={busy} title={t('accounts.loginAsHint')}>
                  {t('accounts.loginAs')}
                </button>
              )}
              {(a.upstream.provisionError || !a.upstream.userId) && (
                <button className="um-btn" onClick={() => retryProvision(a)} disabled={busy}>
                  {t('accounts.retryProvision')}
                </button>
              )}
              <button className="um-btn" onClick={() => open({ kind: 'edit', account: a })} disabled={busy}>
                {t('accounts.edit')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {mode.kind !== 'closed' && (
        <div className="sg acct-form" aria-busy={busy}>
          {busy && (
            <div className="acct-form-overlay">
              <Spinner size={22} />
            </div>
          )}
          <div className="sg-title">
            {mode.kind === 'create' ? t('accounts.newAccount') : t('accounts.editAccount')}
            {mode.kind === 'edit' && <span className="acct-form-subject">@{mode.account.username}</span>}
          </div>

          {/* A disabled fieldset greys out every control (inputs, selects, react-select, buttons) while saving. */}
          <fieldset className="acct-fieldset" disabled={busy}>
          {/* Identity and role */}
          <div className="acct-section">
            <div className="acct-section-title">{t('accounts.sectionIdentity')}</div>
            <div className="sg-grid">
              {mode.kind === 'create' && (
                <InputField
                  label={t('accounts.username')}
                  value={draft.username}
                  onChange={(v) => set('username', v)}
                  hint={t('accounts.usernameHint')}
                  name="account-username"
                  autoComplete="off"
                />
              )}
              <InputField
                label={t('accounts.displayName')}
                value={draft.displayName}
                onChange={(v) => set('displayName', v)}
                name="account-display-name"
                autoComplete="off"
              />
              <SelectField label={t('accounts.role')} value={draft.role} options={roleOptions} onChange={(v) => set('role', v as Role)} />
              {mode.kind === 'edit' && (
                <SelectField
                  label={t('accounts.status')}
                  value={draft.status}
                  options={statusOptions}
                  onChange={(v) => set('status', v as AccountStatus)}
                />
              )}
            </div>
          </div>

          {/* Data access */}
          <div className="acct-section">
            <div className="acct-section-title">{t('accounts.sectionAccess')}</div>
            <CountriesField
              label={t('accounts.countries')}
              hint={t('accounts.countriesHint')}
              value={draft.countries}
              options={countries}
              placeholder={t('accounts.countriesPlaceholder')}
              onChange={(v) => set('countries', v)}
            />
            {mode.kind === 'create' && (
              <InputField
                label={t('accounts.linkUpstream')}
                value={draft.upstreamUserId}
                onChange={(v) => set('upstreamUserId', v)}
                hint={t('accounts.linkUpstreamHint')}
                name="account-upstream-user-id"
                autoComplete="off"
              />
            )}
          </div>

          {/* Password — `new-password` keeps the browser from filling the admin's own login credentials here. */}
          <div className="acct-section">
            <div className="acct-section-title">{t('accounts.sectionPassword')}</div>
            <div className="sg-grid">
              <InputField
                label={mode.kind === 'create' ? t('accounts.password') : t('accounts.newPassword')}
                type="password"
                value={draft.password}
                onChange={(v) => set('password', v)}
                hint={mode.kind === 'create' ? t('accounts.passwordHint') : t('accounts.newPasswordHint')}
                name="account-new-password"
                autoComplete="new-password"
              />
              {mode.kind === 'edit' && (
                <InputField
                  label={t('accounts.currentPassword')}
                  type="password"
                  value={draft.currentPassword}
                  onChange={(v) => set('currentPassword', v)}
                  hint={t('accounts.currentPasswordHint')}
                  name="admin-current-password"
                  autoComplete="current-password"
                  readOnly={!draft.password}
                />
              )}
            </div>
          </div>

          <div className="acct-actions">
            <div className="acct-actions-left">
              {/* Danger zone: only for other accounts (an admin can never delete themselves, server-enforced too). */}
              {mode.kind === 'edit' &&
                me?.id !== mode.account.id &&
                (confirmDelete ? (
                  <>
                    <span className="acct-danger-hint">{t('accounts.deleteHint')}</span>
                    <button className="btn-danger solid" onClick={() => remove(mode.account)} disabled={busy}>
                      {t('accounts.deleteConfirm')}
                    </button>
                    <button className="btn-cancel" onClick={() => setConfirmDelete(false)} disabled={busy}>
                      {t('accounts.keep')}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-danger"
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                    title={t('accounts.deleteHint')}
                  >
                    {t('accounts.delete')}
                  </button>
                ))}
              {formError && (
                <div className="acct-form-error" role="alert">
                  {formError}
                </div>
              )}
            </div>
            <div className="acct-buttons">
              <button className="btn-cancel" onClick={close} disabled={busy}>
                {t('saveBar.cancel')}
              </button>
              <button className="btn-save" onClick={submit} disabled={busy}>
                {busy ? t('saveBar.saving') : mode.kind === 'create' ? t('accounts.createButton') : t('saveBar.saveChanges')}
              </button>
            </div>
          </div>
          </fieldset>
        </div>
      )}

      <div className="sg">
        <div className="sg-title">{t('accounts.rolePermissions')}</div>
        <table className="role-table">
          <thead>
            <tr>
              <th className="cap">{t('accounts.capability')}</th>
              {ROLES.map((role) => (
                <th key={role}>{t(`roles.${role}`) || ROLE_LABELS[role]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.id}>
                <td className="cap">{t(`roles.cap.${cap.id}`)}</td>
                {ROLES.map((role) => {
                  const ok = ROLES.indexOf(role) >= ROLES.indexOf(cap.minRole);
                  return (
                    <td key={role} className={ok ? 'yes' : 'no'}>
                      {ok ? '✓' : '✗'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AccountsTab;
